import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Persist a single piece of UI state in the URL query string so that tab,
 * search, filter, sort, and view selections survive navigation and reloads.
 * The value lives entirely in `?key=...`; an empty/default value drops the
 * param to keep URLs clean.
 */
export function useUrlState(
  key: string,
  defaultValue: string
): [string, (next: string) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) ?? defaultValue;

  const setValue = useCallback(
    (next: string) => {
      setParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          if (!next || next === defaultValue) {
            updated.delete(key);
          } else {
            updated.set(key, next);
          }
          return updated;
        },
        { replace: true }
      );
    },
    [key, defaultValue, setParams]
  );

  return [value, setValue];
}
